import { useEffect, useState } from 'react';
import { useListContext, useDataProvider, useNotify, useResourceContext, useRedirect } from 'react-admin';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Button, Typography, Stack, Skeleton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';

/**
 * Drag-to-reorder table for resources with a `sortOrder` integer column.
 * Drops update sortOrder on each affected row via dataProvider.update.
 *
 * Props:
 *   columns: [{ key, header, render(record) → ReactNode, width? }, ...]
 */
function SortableRow({ record, columns, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: record.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f4e7c2' : undefined,
  };
  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 40, p: 1 }}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }} aria-label="Drag to reorder">
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.key} sx={{ width: col.width }}>
          {col.render(record)}
        </TableCell>
      ))}
      <TableCell sx={{ width: 80, textAlign: 'right' }}>
        <IconButton size="small" onClick={() => onEdit(record.id)} aria-label="Edit">
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export function ReorderableDatagrid({ columns }) {
  const { data, isLoading, refetch } = useListContext();
  const resource = useResourceContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data ? [...data] : []);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;

    const reordered = arrayMove(items, oldIdx, newIdx);
    setItems(reordered);

    setSaving(true);
    try {
      // Persist sortOrder for every row that changed.
      const updates = reordered
        .map((item, idx) => ({ item, newOrder: idx + 1 }))
        .filter(({ item, newOrder }) => item.sortOrder !== newOrder);

      await Promise.all(
        updates.map(({ item, newOrder }) =>
          dataProvider.update(resource, {
            id: item.id,
            data: { sortOrder: newOrder },
            previousData: item,
          }),
        ),
      );
      notify('Order saved', { type: 'success', autoHideDuration: 1500 });
      refetch();
    } catch (err) {
      notify(`Failed to save order: ${err.message}`, { type: 'error' });
      setItems(data);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[0, 1, 2].map((i) => <Skeleton key={i} height={48} sx={{ my: 1 }} />)}
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No items yet.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => redirect('create', resource)}>
          Create the first one
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Drag the handle in each row to reorder. {saving && '· Saving…'}
        </Typography>
      </Stack>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ width: col.width, fontWeight: 600 }}>
                  {col.header}
                </TableCell>
              ))}
              <TableCell sx={{ width: 80 }} />
            </TableRow>
          </TableHead>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <TableBody>
              {items.map((record) => (
                <SortableRow
                  key={record.id}
                  record={record}
                  columns={columns}
                  onEdit={(id) => redirect('edit', resource, id)}
                />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
      </DndContext>
    </Box>
  );
}

/** Common column renderers */
export const imageCell = (src, alt = '') => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 0.5, background: '#eee' }}
    onError={(e) => { e.target.style.opacity = 0.3; }}
  />
);
